import { DatePickerValue, parseOutput, PickerMode } from './control-value';
import { MINUTES_PER_HOUR } from './date-picker.constants';
import {
  CompiledPattern,
  EMPTY_FIELD_VALUES,
  FieldValues,
  ParseResult,
  RangeSide,
} from './date-picker.model';
import { clearSlots, slotsAreEmpty, slotsFromValues } from './slot-layout';
import { parseRangeSlots, parseSlots } from './value-parser';

/**
 * The picker's decisions that depend on its inputs but not on the DOM: which error the slots
 * report, how a calendar click lands in a range, and when two control values mean the same.
 */

export interface ErrorKeyContext {
  mode: PickerMode;
  minDate: Date;
  maxDate: Date;
  /** Whether a bound was set at all — a time is only checked against one that was. */
  hasMinDate: boolean;
  hasMaxDate: boolean;
}

export function errorKeyOf(
  pattern: CompiledPattern,
  slots: readonly string[],
  context: ErrorKeyContext,
): string | null {
  if (!slots.length || slotsAreEmpty(slots)) return null;

  if (context.mode === PickerMode.Range) {
    const { from, to } = parseRangeSlots(pattern, slots);
    const partial = (!from.empty && !from.complete) || (!to.empty && !to.complete);
    return partial ? 'invalid' : null;
  }

  const parsed = parseSlots(pattern, slots);
  if (!parsed.complete || parsed.issues.length) return 'invalid';

  if (context.mode === PickerMode.Time) return timeBoundErrorKey(parsed.values, context);

  const date = parsed.date;
  if (!date) return 'invalid';

  if (date.getTime() < context.minDate.getTime()) return 'minDate';
  if (date.getTime() > context.maxDate.getTime()) return 'maxDate';

  return null;
}

function timeBoundErrorKey(values: FieldValues, context: ErrorKeyContext): string | null {
  const minutes = (values.hour24 ?? 0) * MINUTES_PER_HOUR + (values.minute ?? 0);
  const min = context.hasMinDate ? minutesOfDay(context.minDate) : null;
  const max = context.hasMaxDate ? minutesOfDay(context.maxDate) : null;

  if (min !== null && minutes < min) return 'minTime';
  if (max !== null && minutes > max) return 'maxTime';

  return null;
}

const minutesOfDay = (date: Date): number => date.getHours() * MINUTES_PER_HOUR + date.getMinutes();

export function dateToValues(date: Date): FieldValues {
  return {
    ...EMPTY_FIELD_VALUES,
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  };
}

/**
 * A calendar click in range mode. The first click — or one after a complete range — starts a
 * new range; the second completes it, in order whichever end was clicked first.
 */
export function pickRangeDate(
  pattern: CompiledPattern,
  slots: readonly string[],
  range: { from: ParseResult; to: ParseResult },
  date: Date,
): { slots: readonly string[]; complete: boolean } {
  const { from, to } = range;

  if ((from.date && to.date) || (!from.date && !to.date)) {
    const cleared = clearSlots(pattern, slots, RangeSide.To);
    return {
      slots: slotsFromValues(pattern, cleared, dateToValues(date), RangeSide.From, true),
      complete: false,
    };
  }

  const anchor = from.date ?? to.date!;
  const [start, end] = anchor.getTime() <= date.getTime() ? [anchor, date] : [date, anchor];

  let next = slotsFromValues(pattern, slots, dateToValues(start), RangeSide.From, true);
  next = slotsFromValues(pattern, next, dateToValues(end), RangeSide.To, true);

  return { slots: next, complete: true };
}

export function toDate(value: string | Date | null): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  const parsed = parseOutput(value, PickerMode.DateTime) ?? parseOutput(value, PickerMode.Date);
  if (!parsed || parsed.year == null || parsed.month == null || parsed.day == null) return null;

  return new Date(
    parsed.year,
    parsed.month - 1,
    parsed.day,
    parsed.hour24 ?? 0,
    parsed.minute ?? 0,
  );
}

/**
 * Empty has four spellings across the forms this control is bound to — `null`,
 * `undefined`, `''`, and a range whose ends are any of those. They all mean the same
 * thing, so a round trip through a form that respells it is not a change.
 */
export function isEmptyValue(value: DatePickerValue): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return !value.trim();
  return !value.from && !value.to;
}

const sameEnd = (a: string | null | undefined, b: string | null | undefined): boolean =>
  (a || null) === (b || null);

export function valuesEqual(a: DatePickerValue, b: DatePickerValue): boolean {
  if (a === b) return true;

  const aEmpty = isEmptyValue(a);
  const bEmpty = isEmptyValue(b);
  if (aEmpty || bEmpty) return aEmpty && bEmpty;

  if (typeof a === 'string' || typeof b === 'string') {
    return typeof a === 'string' && typeof b === 'string' && a.trim() === b.trim();
  }

  return sameEnd(a?.from, b?.from) && sameEnd(a?.to, b?.to);
}
